import React, { useState } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSeller } from "../context/SellerContext";
import { usePasswordToggle } from "../hooks/usePasswordToggle";
import GuestOrderLookup from "../components/Login/GuestOrderLookup";
import SellerBusinessInfo from "../components/SellerBusinessInfo";
import "../styles/common.css";

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { seller, isSellerSite, loading: sellerLoading } = useSeller();
  
  // Tab State: 'member' or 'guest'
  const [activeTab, setActiveTab] = useState('member');

  // Member Login State
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  

  // 소셜 로그인 실패 시 메인 도메인 콜백에서 되돌아오며 전달되는 안내 문구
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(searchParams.get("authError") || "");
  const [loading, setLoading] = useState(false);

  const { inputType: pwType, toggle: togglePw, icon: pwIcon } = usePasswordToggle();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const user = await login(formData.username, formData.password, seller?.id);

      if (user.role === "ADMIN") {
        setError("관리자는 관리자 로그인 페이지를 이용해주세요.");
        setLoading(false);
        return;
      }

      navigate("/");
    } catch (err) {
      setError("로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.");
    } finally {
      setLoading(false);
    }
  };


  // 서브몰이 아닌 경우 로그인 페이지 접근 불가 → 홈으로 리다이렉트
  if (!sellerLoading && !isSellerSite) {
    return <Navigate to="/" />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background-light dark:bg-background-dark py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-md w-full space-y-8 bg-white dark:bg-[#162a1c] p-8 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800">
        
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            {isSellerSite && seller?.logoUrl ? (
                <img src={seller.logoUrl} alt={seller.shopName} className="h-16 w-auto object-contain rounded-xl" />
            ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-contrast">
                    <span className="material-symbols-outlined text-3xl">local_florist</span>
                </div>
            )}
          </div>
          <h1 className="text-3xl font-extrabold text-[#0d1b12] dark:text-white tracking-tight">
            {isSellerSite && seller?.shopName ? seller.shopName : "플라워 플랫폼"}
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'member' && '회원 서비스 이용을 위해 로그인해주세요'}
            {activeTab === 'guest' && '비회원으로 주문하신 내역을 조회합니다'}
            {activeTab === 'findOrder' && '잃어버린 주문번호를 찾아드립니다'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button 
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors duration-300 focus:outline-none ${
                    activeTab === 'member' 
                    ? 'border-primary text-primary-strong'
                    : 'border-transparent text-gray-400 hover:text-gray-500 hover:border-gray-300'
                }`}
                onClick={() => { setActiveTab('member'); setError(''); }}
            >
                회원 로그인
            </button>
            <button 
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors duration-300 focus:outline-none ${
                    activeTab === 'guest' 
                    ? 'border-primary text-primary-strong'
                    : 'border-transparent text-gray-400 hover:text-gray-500 hover:border-gray-300'
                }`}
                onClick={() => { setActiveTab('guest'); setError(''); }}
            >
                비회원 조회
            </button>
        </div>

        {/* Member Login Form */}
        {activeTab === 'member' && (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl text-sm text-center">
                    {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    아이디
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    autoFocus
                    className="appearance-none relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-700 placeholder-gray-400 text-gray-900 dark:text-white dark:bg-[#0d1b12] rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10 sm:text-sm transition-colors"
                    placeholder="아이디를 입력하세요"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    비밀번호
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={pwType}
                      required
                      className="appearance-none relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-700 placeholder-gray-400 text-gray-900 dark:text-white dark:bg-[#0d1b12] rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10 sm:text-sm transition-colors pr-10"
                      placeholder="비밀번호를 입력하세요"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer z-20"
                      onClick={togglePw}
                      tabIndex="-1"
                    >
                      <span className="material-symbols-outlined text-xl">
                        {pwIcon}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-primary-contrast bg-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all shadow-lg shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                      <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-primary-contrast" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          로그인 중...
                      </span>
                  ) : "로그인"}
                </button>
              </div>

              {/* 소셜 로그인 */}
              <div className="relative pt-2">
                <div className="absolute inset-x-0 top-1/2 h-px bg-gray-100 dark:bg-gray-800"></div>
                <div className="relative flex justify-center">
                  <span className="bg-white dark:bg-[#162a1c] px-4 text-xs text-gray-400">또는 간편 로그인</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const mainHub = process.env.REACT_APP_MAIN_HUB_URL || window.location.origin;
                    const returnTo = encodeURIComponent(window.location.origin);
                    window.location.href = `${mainHub}/auth/kakao?returnTo=${returnTo}`;
                  }}
                  className="flex items-center justify-center gap-2 bg-[#FEE500] text-[#3c1e1e] py-3 rounded-xl font-bold text-sm hover:brightness-95 transition-all"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z"/>
                  </svg>
                  카카오
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const mainHub = process.env.REACT_APP_MAIN_HUB_URL || window.location.origin;
                    const returnTo = encodeURIComponent(window.location.origin);
                    window.location.href = `${mainHub}/auth/naver?returnTo=${returnTo}`;
                  }}
                  className="flex items-center justify-center gap-2 bg-[#03C75A] text-white py-3 rounded-xl font-bold text-sm hover:brightness-95 transition-all"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.273 12.845L7.376 0H0v24h7.726V11.156L16.624 24H24V0h-7.727v12.845z"/>
                  </svg>
                  네이버
                </button>
              </div>

              <div className="flex items-center justify-between text-sm">
                <Link to="/signup" className="font-medium text-primary-strong hover:text-primary/80 transition-colors">
                  계정이 없으신가요? 회원가입
                </Link>
                <Link to="/" className="font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors">
                  홈으로
                </Link>
              </div>
            </form>
        )} 
        
        {/* Guest Order Lookup & Find Order Number */}
        <GuestOrderLookup activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      {/* Seller Information Footer - 빈 필드는 자동 숨김 (개인 셀러 지원) */}
      {isSellerSite && (
          <SellerBusinessInfo
            seller={seller}
            align="center"
            className="mt-8 text-xs text-gray-500 dark:text-gray-400 max-w-lg"
          />
      )}
    </div>
  );
}

export default Login;
